/* ==========================================================================
   Royal Honey — collection & search
   Filter drawer (live results) · sorting · grid density · removable chips · load more
   ========================================================================== */
(() => {
  'use strict';

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const debounce = (fn, wait = 350) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };

  define('facet-filters', class extends HTMLElement {
    connectedCallback() {
      this.sheet = this.querySelector('[data-filters]');
      this.form = this.querySelector('[data-facets-form]');
      this.sort = this.querySelector('[data-sort]');
      const apply = debounce(() => this.apply(), 380);
      this.form?.addEventListener('change', apply);
      this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.apply(); });
      this.sort?.addEventListener('change', () => this.apply());
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); this.load(link.href); return; }
        if (e.target.closest('[data-filters-close]')) { e.preventDefault(); this.closeSheet(); }
        const more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); this.loadMore(more); }
      });
      this.sheet?.addEventListener('toggle', () => document.body.classList.toggle('drawer-open', this.sheet.open && window.matchMedia('(max-width: 1099px)').matches));
      this.querySelectorAll('[data-density]').forEach((btn) => btn.addEventListener('click', () => this.density(btn.dataset.density)));
      let saved = null;
      try { saved = window.localStorage.getItem('sr-density'); } catch (err) { saved = null; }
      if (saved) this.density(saved, false);
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.sheet?.open) { this.closeSheet(); this.sheet.querySelector('summary')?.focus(); } });
      window.addEventListener('popstate', () => this.load(window.location.href, false));
    }
    closeSheet() { if (this.sheet) this.sheet.open = false; }
    density(value, remember = true) {
      const grid = this.querySelector('[data-grid]');
      if (!grid || !value) return;
      grid.style.setProperty('--cols-desktop', value);
      grid.style.setProperty('--cols-tablet', Math.min(Number(value), 3));
      this.querySelectorAll('[data-density]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.density === value)));
      if (remember) { try { window.localStorage.setItem('sr-density', value); } catch (err) { /* private mode */ } }
    }
    url() {
      const params = new URLSearchParams(new FormData(this.form || undefined));
      if (this.sort && !this.form) params.set('sort_by', this.sort.value);
      [...params.keys()].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      params.delete('page');
      return `${window.location.pathname}?${params.toString()}`;
    }
    apply() { this.load(this.url()); }
    async load(url, push = true) {
      const results = this.querySelector('[data-results]');
      results?.classList.add('is-loading');
      try {
        const res = await fetch(url, { headers: { Accept: 'text/html' } });
        const doc = parse(await res.text());
        const fresh = doc.querySelector('facet-filters');
        if (!fresh) { window.location.href = url; return; }
        const active = document.activeElement;
        const focusKey = active && active.name ? `${active.name}::${active.value}` : null;
        ['[data-results]', '[data-active-facets]', '[data-results-count]', '[data-facets-list]', '[data-results-total]'].forEach((sel) => {
          const src = fresh.querySelector(sel);
          const dst = this.querySelector(sel);
          if (src && dst) dst.innerHTML = src.innerHTML;
        });
        const badge = this.querySelector('[data-filter-count]');
        const freshBadge = fresh.querySelector('[data-filter-count]');
        if (badge && freshBadge) { badge.textContent = freshBadge.textContent; badge.hidden = freshBadge.hidden; }
        if (focusKey) {
          const [name, value] = focusKey.split('::');
          [...this.querySelectorAll('input')].find((i) => i.name === name && i.value === value)?.focus();
        }
        if (push) window.history.replaceState({}, '', url);
        let saved = null;
        try { saved = window.localStorage.getItem('sr-density'); } catch (err) { saved = null; }
        if (saved) this.density(saved, false);
        window.SR?.utils?.bus.emit('cards:loaded', this);
      } catch (err) {
        window.location.href = url;
      } finally {
        this.querySelector('[data-results]')?.classList.remove('is-loading');
      }
    }
    async loadMore(button) {
      if (button.classList.contains('is-loading')) return;
      button.classList.add('is-loading');
      try {
        const res = await fetch(button.href);
        const doc = parse(await res.text());
        const grid = this.querySelector('[data-grid]');
        doc.querySelectorAll('[data-grid] > li').forEach((li) => grid?.appendChild(li));
        const freshMore = doc.querySelector('[data-more]');
        const more = this.querySelector('[data-more]');
        if (freshMore && more) more.replaceWith(freshMore); else more?.remove();
        window.SR?.utils?.bus.emit('cards:loaded', this);
      } catch (err) {
        window.location.href = button.href;
      }
    }
  });
})();
